import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  LOCALE_ID,
  OnInit,
  ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SubscriptionService } from '@core/subscription/subscription.service';
import { SubscriptionQuery } from '@core/subscription/subscription.query';
import {
  SubscriptionTier,
  Subscription,
  PlanChangePreview,
  PendingPlanChange,
  isFreeTierSubscription,
  hasSignedOnboardingContract,
} from '@core/models/subscription.model';
import { OrganizationQuery } from '@core/organization/organization.query';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';
import { AppStoreQuery } from '@core/state/app-state.query';
import { RouterService } from '@core/router/router.service';
import { selectCompanyAbsoluteRoute, selectPartnerCompanyAbsoluteRoute, partnerRoute, productRoute } from '@shared/shared-routes';
import { Observable, of, BehaviorSubject, combineLatest, timer, Subscription as RxjsSubscription } from 'rxjs';
import { catchError, exhaustMap, filter, finalize, map, shareReplay, switchMap, tap, take, withLatestFrom } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TypedFormGroup } from '@feature/forms/form-model';
import { isPaymentMethodRequiredError, StripeService } from '@core/stripe/stripe.service';
import { User } from '@core/models/user.model';
import { isNoSubscriptionFoundError } from '@core/subscription/subscription-error.util';
import {
  isOnboardingContractNoOrganization,
  isOnboardingContractNotSigned,
  isOnboardingContractSigningUnavailable,
  isOnboardingContractTemplateMissing,
  OnboardingContractService,
} from '@core/subscription/onboarding-contract.service';
import { TranslateService } from '@ngx-translate/core';

export interface SubscriptionUpgradeDialogData {
  currentSubscription?: Subscription;
  organizationType?: 'gp' | 'lp';
  defaultTierUid?: string | null;
  /** Scroll straight to the GP add-on selector on open (KAP-1849 fix for the
   * header's "Purchase Add-ons" entry point, which previously opened a
   * dedicated add-ons-only view rather than the full tier ladder). */
  focusAddons?: boolean;
}

export interface TierComparisonRow {
  feature: string;
  free: string | boolean;
  standard: string | boolean;
  professional: string | boolean;
  enterprise: string | boolean;
}

@UntilDestroy()
@Component({
  selector: 'wai-subscription-upgrade-dialog',
  templateUrl: './subscription-upgrade-dialog.component.html',
  styleUrls: ['./subscription-upgrade-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SubscriptionUpgradeDialogComponent implements OnInit, AfterViewInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @ViewChild('gpAddonSection') private gpAddonSectionRef?: ElementRef<HTMLElement>;
  /**
   * The plan-change preview renders below the tier cards, out of sight in the
   * scrolling dialog, while its tier button is hidden — so the click looked like
   * it just made the button disappear (KAP-1897). Bring it into view as it appears.
   */
  @ViewChild('planChangePreviewBlock') set planChangePreviewBlock(element: ElementRef<HTMLElement> | undefined) {
    element?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  public availableTiers$: Observable<SubscriptionTier[]>;
  public currentSubscription: Subscription | null = null;
  public isLP: boolean = false;
  public isGP: boolean = false;
  public isLoading: boolean = false;
  public selectedTierUid: string | null = null;
  private selectedTierCode: string | null = null;
  private selectedTierUid$ = new BehaviorSubject<string | null>(null);
  public comparisonRows: TierComparisonRow[] = [];
  /**
   * Whether the selected tier offers a free trial. Backed by the tier's
   * `isTrialAvailable` flag rather than a `lp_standard` code check — every paid tier
   * carries a trial as of KAP-1853, and eligibility is server-side configuration.
   */
  public isTrialTierSelected$: Observable<boolean>;
  public getSelectedTier$: Observable<SubscriptionTier | null>;
  public hasNoSubscriptionError: boolean = false;
  /**
   * True until `currentSubscription` reflects a definitive answer for a brand-new
   * GP org: either a real subscription came back, or `performGpFreeAutoUpgrade()`
   * finished (successfully or not). Gates the bottom Continue button on the
   * `!currentSubscription` branch of `canSubscribe()` — without it, that button
   * renders as clickable during this window while `onProceed()` still no-ops.
   */
  public isResolvingSubscription: boolean = true;
  public planChangePreview: PlanChangePreview | null = null;
  public pendingPlanChange: PendingPlanChange | null = null;
  public currentStep: 'tier' | 'contract' | 'sepa' = 'tier';
  public contractSigningEmbed: string | null = null;
  public contractSigningUnavailable = false;
  public isContractBusy = false;
  public selectedBillingInterval: 'annual' | 'monthly' = 'annual';
  public pendingSepaPaymentMethodId: string | null = null;
  /** The backend refused a trial for this subscribe, so the SEPA step charges now. */
  public sepaChargesNow = false;
  private sepaTargetTierCode: string | null = null;
  private sepaTargetBillingInterval: 'annual' | 'monthly' = 'annual';
  private pendingAfterContract: (() => void) | null = null;
  private hasConfirmedSignedContract = false;
  /** True when POST create returned 404 (no template on this env/org). Blocks tier flow. */
  public contractTemplateMissing = false;
  private isEnsuringOnboardingContract = false;
  private previousDisableClose = true;
  private contractPollSub: RxjsSubscription | null = null;
  private readonly contractPollMs = 8000;
  public billingContactName = '';
  public billingContactEmail = '';

  public displayedColumns: string[] = ['feature', 'free', 'standard', 'professional', 'enterprise'];
  private readonly lpAnnualFallbackByCode: Record<string, number> = {
    lp_free: 0,
    lp_standard: 12000,
    lp_professional: 36000,
    lp_enterprise: 60000,
  };
  // KAP-1849. GPTierPrice rows aren't seeded by default (same situation as
  // LPTierPrice), so this fallback mirrors GP_DEFAULT_MONTHLY_PRICES
  // (stripe_billing_service.py) in EUR, not cents.
  private readonly gpMonthlyFallbackByCode: Record<string, number> = {
    gp_free: 0,
    gp_basic: 1000,
    gp_professional: 3000,
    gp_enterprise: 5000,
  };
  // GP quota-per-tier fallback for the same reason, mirrors create_gp_feature_limits.
  private readonly gpQuotaFallbackByCode: Record<string, number> = {
    gp_free: 0,
    gp_basic: 10,
    gp_professional: 40,
    gp_enterprise: 100,
  };
  private readonly gpTierOrder = ['gp_free', 'gp_basic', 'gp_professional', 'gp_enterprise'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SubscriptionUpgradeDialogData,
    private dialogRef: MatDialogRef<SubscriptionUpgradeDialogComponent>,
    private subscriptionService: SubscriptionService,
    private subscriptionQuery: SubscriptionQuery,
    private organizationQuery: OrganizationQuery,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private appStoreQuery: AppStoreQuery,
    private routerService: RouterService,
    private stripeService: StripeService,
    private onboardingContractService: OnboardingContractService,
    private translate: TranslateService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.availableTiers$ = of([]);
    this.isTrialTierSelected$ = of(false);
    this.getSelectedTier$ = of(null);

    this.currentSubscription = data.currentSubscription || this.subscriptionQuery.getCurrentSubscription();
    this.isLP = data.organizationType === 'lp' || false;
    this.isGP = data.organizationType === 'gp' || false;

    if (!this.isLP && !this.isGP) {
      this.organizationQuery
        .selectChosenOrganizationType()
        .pipe(
          untilDestroyed(this),
          map((type) => {
            this.isLP = type === 'investor' || type === 'client';
            this.isGP = type === 'emittent';
            if (!this.currentSubscription) {
              this.currentSubscription = this.subscriptionQuery.getCurrentSubscription();
            }
            this.cdr.markForCheck();
          }),
        )
        .subscribe();
    }

    const currentUser = this.appStoreQuery.getValue()?.user as User | undefined;
    if (currentUser) {
      this.billingContactName = currentUser.getFullName() || '';
      this.billingContactEmail = currentUser.email || '';
    }
  }

  ngOnInit(): void {
    this.selectedBillingInterval = this.currentSubscription?.billingInterval || 'annual';

    this.dialogRef
      .beforeClosed()
      .pipe(take(1), untilDestroyed(this))
      .subscribe(() => this.stopContractPolling());

    if (this.isLP) {
      this.loadLPTiers();
      this.loadCurrentSubscription();
      this.loadPendingPlanChange();
      this.setupLPComparisonTable();
      this.prefetchOnboardingContractStatus();
    } else if (this.isGP) {
      this.loadGPTiers();
      this.loadCurrentSubscription();
      this.setupGPComparisonTable();
      this.prefetchOnboardingContractStatus();
    }
  }

  ngAfterViewInit(): void {
    // KAP-1849 fix: the header's "Purchase Add-ons" entry point used to open a
    // dedicated add-ons-only view; it now opens the same full tier dialog as
    // everything else, so scroll straight to the add-on section instead of
    // making the user hunt for it below the tier ladder. setTimeout lets the
    // dialog's own open animation/layout settle before scrolling.
    if (this.data.focusAddons && this.isGP) {
      setTimeout(() => {
        this.gpAddonSectionRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  /** GET status on open — if already signed, skip contract gate on Upgrade. */
  private prefetchOnboardingContractStatus(): void {
    this.onboardingContractService
      .getStatus()
      .pipe(
        take(1),
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe((status) => {
        if (status && hasSignedOnboardingContract(status)) {
          this.hasConfirmedSignedContract = true;
          this.cdr.markForCheck();
        }
      });
  }

  private loadLPTiers(): void {
    this.availableTiers$ = this.subscriptionQuery.selectAvailableTiers('lp').pipe(
      take(1),
      switchMap((cachedTiers) => {
        return this.subscriptionService.getAvailableTiers('lp', true).pipe(
          catchError(() => of(cachedTiers || [])),
        );
      }),
      tap((tiers) => {
        if (this.selectedTierUid) {
          const stillAvailable = tiers.find((t) => t.uid === this.selectedTierUid);
          if (stillAvailable) {
            this.selectedTierUid$.next(this.selectedTierUid);
            this.cdr.markForCheck();
            return;
          }
        }
        if (this.data.defaultTierUid) {
          const defaultTier = tiers.find((t) => t.uid === this.data.defaultTierUid);
          if (defaultTier) {
            this.selectedTierUid = defaultTier.uid;
            this.selectedTierUid$.next(defaultTier.uid);
            this.cdr.markForCheck();
            return;
          }
        }
        const nextTier = this.getNextTier(tiers);
        if (nextTier) {
          this.selectedTierUid = nextTier.uid;
          this.selectedTierUid$.next(nextTier.uid);
        } else {
          this.selectedTierUid$.next(null);
        }
        this.cdr.markForCheck();
      }),
      catchError(() => {
        this.snackBarService.error('INTL_ERROR_LOADING_TIERS');
        return of([]);
      }),
      // The template's async pipes, updateTierObservables()'s own subscribe,
      // and continueUpgradeAfterContract() (LP) each subscribe independently -
      // without sharing, every one of them would re-run the HTTP fetch and the
      // tap() side effect above from scratch. refCount: true tears it down
      // once the dialog (and its last subscriber) is gone, same as an
      // unshared cold observable would.
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.updateTierObservables();
  }

  private loadGPTiers(): void {
    this.availableTiers$ = this.subscriptionQuery.selectAvailableTiers('gp').pipe(
      take(1),
      switchMap((cachedTiers) => {
        if (cachedTiers && cachedTiers.length > 0) {
          return of(cachedTiers);
        }
        return this.subscriptionService.getAvailableTiers('gp');
      }),
      tap((tiers) => {
        // Preserve a still-valid selection rather than forcing gp_free on every
        // subscription - availableTiers$ is a cold pipeline that other call sites
        // (e.g. continueGPUpgradeAfterContract) re-subscribe to mid-flow, and an
        // unconditional reset here would clobber whatever tier the user picked.
        if (this.selectedTierUid) {
          const stillAvailable = tiers.find((t) => t.uid === this.selectedTierUid);
          if (stillAvailable) {
            this.selectedTierUid$.next(this.selectedTierUid);
            this.cdr.markForCheck();
            return;
          }
        }
        if (this.data.defaultTierUid) {
          const defaultTier = tiers.find((t) => t.uid === this.data.defaultTierUid);
          if (defaultTier) {
            this.selectedTierUid = defaultTier.uid;
            this.selectedTierUid$.next(defaultTier.uid);
            this.cdr.markForCheck();
            return;
          }
        }
        const freeTier = tiers.find((t) => t.code === 'gp_free');
        if (freeTier) {
          this.selectedTierUid = freeTier.uid;
          this.selectedTierUid$.next(freeTier.uid);
          this.cdr.markForCheck();
        }
      }),
      catchError(() => {
        this.snackBarService.error('INTL_ERROR_LOADING_TIERS');
        return of([]);
      }),
      // See the matching comment in loadLPTiers - shared so the template's
      // async pipes, updateTierObservables(), setupGPComparisonTable(), and
      // continueGPUpgradeAfterContract()'s re-subscribe all reuse one fetch
      // instead of re-running the HTTP call and tap() side effect each time.
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.updateTierObservables();
  }

  private updateTierObservables(): void {
    this.isTrialTierSelected$ = combineLatest([this.availableTiers$, this.selectedTierUid$]).pipe(
      map(([tiers, selectedUid]) => {
        if (!selectedUid || !tiers || tiers.length === 0) {
          return false;
        }
        return this.isTrialAvailableForTier(tiers.find((t) => t.uid === selectedUid) ?? null);
      }),
    );

    this.getSelectedTier$ = combineLatest([this.availableTiers$, this.selectedTierUid$]).pipe(
      map(([tiers, selectedUid]) => {
        if (!selectedUid || !tiers || tiers.length === 0) {
          return null;
        }
        return tiers.find((t) => t.uid === selectedUid) || null;
      }),
    );

    // Synchronous mirror of getSelectedTier$'s code, for canSubscribe() (a plain
    // method, not template-async-pipe-bound) to gate the bottom "Continue"
    // button on gp_free specifically without threading tier data through it.
    this.getSelectedTier$.pipe(untilDestroyed(this)).subscribe((tier) => {
      this.selectedTierCode = tier?.code ?? null;
    });
  }

  private loadCurrentSubscription(): void {
    if (this.isLP) {
      this.subscriptionService
        .getCurrentSubscription(true)
        .pipe(
          untilDestroyed(this),
          tap((subscription) => {
            this.currentSubscription = subscription;
            if (!subscription) {
              this.hasNoSubscriptionError = true;
            } else {
              this.hasNoSubscriptionError = false;
            }
            this.isResolvingSubscription = false;
            this.cdr.markForCheck();
          }),
          catchError((error) => {
            const isNoSubscriptionError = isNoSubscriptionFoundError(error) && (this.isLP || this.isGP);

            if (isNoSubscriptionError) {
              this.hasNoSubscriptionError = true;
            } else {
              this.hasNoSubscriptionError = false;
            }
            this.isResolvingSubscription = false;
            this.cdr.markForCheck();
            return of(null);
          }),
        )
        .subscribe();
      return;
    }

    if (this.currentSubscription) {
      this.hasNoSubscriptionError = false;
      this.isResolvingSubscription = false;
      this.syncGpPendingPlanChange();
      return;
    }

    this.subscriptionService
      .getCurrentSubscription()
      .pipe(
        untilDestroyed(this),
        tap((subscription) => {
          this.currentSubscription = subscription;
          this.syncGpPendingPlanChange();
          if (!subscription && (this.isLP || this.isGP)) {
            this.hasNoSubscriptionError = true;
            if (this.isGP) {
              // Stays pending: performGpFreeAutoUpgrade() below resolves it.
              this.autoUpgradeGPToFreeTier();
            } else {
              this.isResolvingSubscription = false;
            }
          } else {
            this.hasNoSubscriptionError = false;
            this.isResolvingSubscription = false;
          }
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          const isNoSubscriptionError = isNoSubscriptionFoundError(error) && (this.isLP || this.isGP);

          if (isNoSubscriptionError) {
            this.hasNoSubscriptionError = true;
            if (this.isGP) {
              // Stays pending: performGpFreeAutoUpgrade() below resolves it.
              this.autoUpgradeGPToFreeTier();
            } else {
              this.isResolvingSubscription = false;
            }
          } else {
            this.hasNoSubscriptionError = false;
            this.isResolvingSubscription = false;
          }
          this.cdr.markForCheck();
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Automatically upgrade GP users to free tier when no subscription is found.
   * Dialog remains open until user clicks "Continue" button.
   */
  private autoUpgradeGPToFreeTier(): void {
    this.ensureOnboardingContract(() => this.performGpFreeAutoUpgrade());
  }

  private performGpFreeAutoUpgrade(): void {
    this.subscriptionService
      .getAvailableTiers('gp')
      .pipe(
        untilDestroyed(this),
        switchMap((tiers) => {
          const freeTier = tiers.find((t) => t.code === 'gp_free');
          if (!freeTier) {
            this.hasNoSubscriptionError = true;
            this.cdr.markForCheck();
            return of(null);
          }

          return this.subscriptionService.upgradeToTier(freeTier.uid).pipe(
            catchError(() => {
              this.hasNoSubscriptionError = true;
              this.currentSubscription = null;
              this.cdr.markForCheck();
              return of(null);
            }),
          );
        }),
        catchError(() => {
          this.hasNoSubscriptionError = true;
          this.cdr.markForCheck();
          return of(null);
        }),
        tap((upgradedSubscription) => {
          if (upgradedSubscription) {
            this.currentSubscription = upgradedSubscription;
            this.hasNoSubscriptionError = false;
            if (upgradedSubscription.tier) {
              this.selectedTierUid = upgradedSubscription.tier.uid;
              this.selectedTierUid$.next(upgradedSubscription.tier.uid);
            }
            this.cdr.markForCheck();
          } else {
            this.hasNoSubscriptionError = true;
            this.cdr.markForCheck();
          }
        }),
        finalize(() => {
          this.isResolvingSubscription = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  private setupLPComparisonTable(): void {
    this.comparisonRows = [
      {
        feature: 'INTL_DDQ_RUNS_PER_MONTH',
        free: '0',
        standard: '3',
        professional: '10',
        enterprise: 'INTL_UNLIMITED',
      },
      {
        feature: 'INTL_IM_GENERATIONS',
        free: '0',
        standard: '1',
        professional: 'INTL_UNLIMITED',
        enterprise: 'INTL_UNLIMITED',
      },
      {
        feature: 'INTL_CUSTOM_TEMPLATES',
        free: false,
        standard: false,
        professional: true,
        enterprise: true,
      },
      {
        feature: 'INTL_IM_UPLOADS',
        free: false,
        standard: false,
        professional: false,
        enterprise: true,
      },
      {
        // Free-trial length per tier. Filled from each tier's configured
        // `trialPeriodDays` once tiers load (KAP-1854) — no longer a hard-coded
        // "1 month". A tier with no trial shows the "no" icon (false).
        feature: 'INTL_TRIAL_PERIOD',
        free: false,
        standard: false,
        professional: false,
        enterprise: false,
      },
    ];

    // Reflect the configured trial length for each tier in the comparison table.
    // Reads from the store query (already populated by loadLPTiers) to avoid a
    // second tiers fetch, and re-emits a new array reference for OnPush + mat-table.
    this.subscriptionQuery
      .selectAvailableTiers('lp')
      .pipe(untilDestroyed(this))
      .subscribe((tiers) => {
        const trialRow = this.comparisonRows.find((row) => row.feature === 'INTL_TRIAL_PERIOD');
        if (!trialRow || !tiers) {
          return;
        }
        const byCode = new Map<string, SubscriptionTier>(tiers.map((tier) => [tier.code, tier]));
        trialRow.free = this.getTrialDurationShortLabel(byCode.get('lp_free'));
        trialRow.standard = this.getTrialDurationShortLabel(byCode.get('lp_standard'));
        trialRow.professional = this.getTrialDurationShortLabel(byCode.get('lp_professional'));
        trialRow.enterprise = this.getTrialDurationShortLabel(byCode.get('lp_enterprise'));
        this.comparisonRows = [...this.comparisonRows];
        this.cdr.markForCheck();
      });
  }

  /**
   * KAP-1849. GP-parallel to setupLPComparisonTable(), but a single row - GP
   * tiers have one metered dimension (LP DDQ uploads/month), not the several
   * LP compares. gp_basic occupies the table's fixed "standard" column slot
   * (TierComparisonRow's column names are an LP-era leftover, reused as-is
   * rather than renamed, to avoid touching the shared table markup/types).
   */
  private setupGPComparisonTable(): void {
    this.comparisonRows = [
      {
        feature: 'INTL_LP_DDQ_UPLOADS_PER_MONTH',
        free: '0',
        standard: '0',
        professional: '0',
        enterprise: '0',
      },
    ];

    this.subscriptionQuery
      .selectAvailableTiers('gp')
      .pipe(untilDestroyed(this))
      .subscribe((tiers) => {
        const row = this.comparisonRows.find((r) => r.feature === 'INTL_LP_DDQ_UPLOADS_PER_MONTH');
        if (!row || !tiers) {
          return;
        }
        const byCode = new Map<string, SubscriptionTier>(tiers.map((tier) => [tier.code, tier]));
        row.free = this.getQuotaDisplayValue(byCode.get('gp_free'));
        row.standard = this.getQuotaDisplayValue(byCode.get('gp_basic'));
        row.professional = this.getQuotaDisplayValue(byCode.get('gp_professional'));
        row.enterprise = this.getQuotaDisplayValue(byCode.get('gp_enterprise'));
        this.comparisonRows = [...this.comparisonRows];
        this.cdr.markForCheck();
      });
  }

  /** -1 = unlimited (GPSubscriptionFeatureLimit.lp_ddq_uploads_per_month convention). */
  private getQuotaDisplayValue(tier: SubscriptionTier | undefined): string {
    const quota = tier?.lpDdqUploadsPerMonth ?? this.gpQuotaFallbackByCode[tier?.code ?? ''] ?? null;
    if (quota === null) {
      return '0';
    }
    return quota === -1 ? 'INTL_UNLIMITED' : String(quota);
  }

  /**
   * Comparison-table cell for a tier's trial length: a localized "{{days}} days
   * free" string, or `false` when the tier has no trial so the table renders its
   * "no" icon. Pre-translated because the generic table cell passes plain strings
   * through the `translate` pipe without interpolation params.
   */
  private getTrialDurationShortLabel(tier: SubscriptionTier | undefined): string | boolean {
    const days = this.getTrialPeriodDays(tier);
    if (!days) {
      return false;
    }
    const key = days === 1 ? 'INTL_TRIAL_FREE_FOR_DAY' : 'INTL_TRIAL_FREE_FOR_DAYS';
    return this.translate.instant(key, { days });
  }

  public onTierSelect(tierUid: string): void {
    this.selectedTierUid = tierUid;
    this.selectedTierUid$.next(tierUid);
    this.planChangePreview = null;
    this.pendingSepaPaymentMethodId = null;
    this.sepaTargetTierCode = null;
    this.sepaChargesNow = false;
    this.currentStep = 'tier';
    this.cdr.markForCheck();
  }

  /**
   * The single canonical tier-code-to-visual-slot mapping, so "GP's Basic
   * tier occupies LP's Standard slot" is expressed once instead of repeated
   * per-consumer (getTierButtonClass, getTierIcon previously each re-derived
   * it independently, and could drift out of sync with each other or with a
   * future tier code).
   */
  private getTierVisualSlot(tierCode: string): 'free' | 'standard' | 'professional' | 'enterprise' {
    const code = tierCode.toLowerCase();
    if (code.includes('free')) {
      return 'free';
    }
    if (code.includes('professional')) {
      return 'professional';
    }
    if (code.includes('enterprise')) {
      return 'enterprise';
    }
    // 'standard' (LP) and 'basic' (GP) share this slot, and it's also the
    // fallback for an unrecognized code.
    return 'standard';
  }

  public getTierButtonClass(tierCode: string): string {
    return `tier-button-${this.getTierVisualSlot(tierCode)}`;
  }

  public getTierDisplayPrice(tier: SubscriptionTier): number | null {
    // GP tiers are monthly-only (KAP-1849) - no annual price, no billing-interval toggle.
    if (tier.tierType === 'gp') {
      return tier.monthlyPriceEur ?? this.gpMonthlyFallbackByCode[tier.code] ?? null;
    }
    const annual = tier.annualPriceEur ?? this.lpAnnualFallbackByCode[tier.code] ?? null;
    const monthly = tier.monthlyPriceEur ?? (annual !== null ? this.estimateMonthlyFromAnnual(annual) : null);
    return this.selectedBillingInterval === 'monthly' ? monthly : annual;
  }

  public hasTierDisplayPrice(tier: SubscriptionTier): boolean {
    return this.getTierDisplayPrice(tier) !== null;
  }

  private estimateMonthlyFromAnnual(annualPriceEur: number): number {
    // Keep UI fallback consistent with backend default monthly markup.
    return Math.round((annualPriceEur / 12 * 1.3) * 100) / 100;
  }

  public onConfirmPlanChange(): void {
    if (!this.planChangePreview || !(this.isLP || this.isGP)) {
      return;
    }

    this.ensureOnboardingContract(() => this.confirmPlanChangeAfterContract());
  }

  private confirmPlanChangeAfterContract(): void {
    if (!this.planChangePreview || !(this.isLP || this.isGP)) {
      return;
    }

    const tierCode = this.planChangePreview.targetTier?.code;
    const billingInterval = this.planChangePreview.billingInterval;
    if (!tierCode || !billingInterval) {
      return;
    }

    const confirm$ = this.isGP ? this.upgradeGpTierConfirmed(tierCode) : this.confirmUpgrade(tierCode, billingInterval);
    confirm$
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          if (this.handleContractNotSigned(error, () => this.confirmPlanChangeAfterContract())) {
            return of(null);
          }
          const errorMessage =
            error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_UPGRADING_SUBSCRIPTION';
          this.snackBarService.error(errorMessage);
          return of(null);
        }),
      )
      .subscribe();
  }

  public onDismissPlanChangePreview(): void {
    this.planChangePreview = null;
    this.cdr.markForCheck();
  }

  public isPlanChangeConfirmPendingForTier(tier: SubscriptionTier): boolean {
    if (!this.planChangePreview || !(this.isLP || this.isGP)) {
      return false;
    }
    if (this.isGP) {
      // GP tiers are monthly-only: there is no billing interval to match.
      return tier.uid === this.selectedTierUid && this.planChangePreview.targetTier?.code === tier.code;
    }
    const billingInterval = this.selectedBillingInterval || this.currentSubscription?.billingInterval || 'annual';
    return (
      tier.uid === this.selectedTierUid &&
      this.planChangePreview.targetTier?.code === tier.code &&
      this.planChangePreview.billingInterval === billingInterval
    );
  }

  public onUpgrade(): void {
    if (!this.selectedTierUid) {
      return;
    }

    this.ensureOnboardingContract(() => this.continueUpgradeAfterContract());
  }

  private continueUpgradeAfterContract(): void {
    if (!this.selectedTierUid) {
      return;
    }

    if (this.isGP) {
      this.continueGPUpgradeAfterContract();
      return;
    }

    if (!this.isLP) {
      this.upgradeTier();
      return;
    }

    this.availableTiers$
      .pipe(
        take(1),
        switchMap((tiers) => {
          const selectedTier = tiers.find((tier) => tier.uid === this.selectedTierUid);
          if (!selectedTier) {
            return of(null);
          }

          const billingInterval = this.selectedBillingInterval;
          const currentTierCode = this.currentSubscription?.tier?.code;
          const tierOrder = ['lp_free', 'lp_standard', 'lp_professional', 'lp_enterprise'];
          const currentIndex = tierOrder.indexOf(currentTierCode);
          const selectedIndex = tierOrder.indexOf(selectedTier.code);

          // No active LP subscription yet for free tier fallback.
          if (!this.currentSubscription || this.hasNoSubscriptionError) {
            if (this.shouldUseSepaSubscribeFlow(selectedTier)) {
              return this.startLpSepaSubscribe(selectedTier, billingInterval);
            }
            this.upgradeTier();
            return of(null);
          }

          if (currentIndex !== -1 && selectedIndex !== -1 && selectedIndex < currentIndex) {
            return this.scheduleDowngrade(selectedTier.code, billingInterval);
          }

          if (this.shouldUseSepaSubscribeFlow(selectedTier)) {
            return this.startLpSepaSubscribe(selectedTier, billingInterval);
          }

          const hasMatchingPreview =
            this.planChangePreview &&
            this.planChangePreview.targetTier?.code === selectedTier.code &&
            this.planChangePreview.billingInterval === billingInterval;

          if (hasMatchingPreview) {
            return this.confirmUpgrade(selectedTier.code, billingInterval);
          }

          return this.previewUpgrade(selectedTier.code, billingInterval).pipe(map(() => null));
        }),
        catchError((error) => {
          if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
            return of(null);
          }
          const errorMessage =
            error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_UPGRADING_SUBSCRIPTION';
          this.snackBarService.error(errorMessage);
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * KAP-1849. GP-parallel to the LP switchMap block above, but simpler: GP
   * tiers are monthly-only (no billing-interval), and GPUpgradeView switches
   * immediately on a single call.
   *
   * KAP-1891: moving up between paid tiers is charged on the spot, so it gets
   * the same preview/confirm two-step as LP - the issuer sees the "Amount due
   * now" first and confirms from the preview block. First-time subscriptions
   * (SEPA mandate step) and downgrades are unchanged.
   */
  private continueGPUpgradeAfterContract(): void {
    // Capture before subscribing, not read live inside the pipe below: belt-
    // and-suspenders against loadGPTiers()'s tap() (which now shareReplay's
    // and only reassigns selectedTierUid when the prior selection is stale),
    // so a change to that guard can't silently reintroduce the tier lookup
    // below reading a clobbered selection out from under the user's click.
    const selectedTierUidAtCall = this.selectedTierUid;
    this.availableTiers$
      .pipe(
        take(1),
        switchMap((tiers) => {
          const selectedTier = tiers.find((tier) => tier.uid === selectedTierUidAtCall);
          if (!selectedTier) {
            return of(null);
          }

          const currentTierCode = this.currentSubscription?.tier?.code;

          if (selectedTier.code === 'gp_free') {
            // A paid tier with active Stripe billing must go through the
            // billing-aware downgrade endpoint so the Stripe subscription is
            // actually cancelled (at period end) - flipping the DB tier alone
            // would leave Stripe still billing while the app shows Free.
            if (currentTierCode && currentTierCode !== 'gp_free' && this.hasActiveStripeBilling()) {
              return this.downgradeGpTier('gp_free');
            }
            // No active paid billing to cancel (new org, or already gp_free) -
            // DB-only, unchanged existing path.
            this.upgradeTier();
            return of(null);
          }

          const currentIndex = this.gpTierOrder.indexOf(currentTierCode);
          const selectedIndex = this.gpTierOrder.indexOf(selectedTier.code);

          const needsSepaMandate =
            !this.currentSubscription ||
            this.hasNoSubscriptionError ||
            currentTierCode === 'gp_free' ||
            !this.hasActiveStripeBilling() ||
            (this.canReactivateCurrentTier() && selectedTier.uid === this.currentSubscription?.tier?.uid);

          if (needsSepaMandate) {
            if (!this.pendingSepaPaymentMethodId) {
              this.sepaTargetTierCode = selectedTier.code;
              this.currentStep = 'sepa';
              this.cdr.markForCheck();
              return of(null);
            }
            const targetTierCode = this.sepaTargetTierCode || selectedTier.code;
            return this.subscribeGpViaSepa(targetTierCode, this.pendingSepaPaymentMethodId);
          }

          if (currentIndex !== -1 && selectedIndex !== -1 && selectedIndex < currentIndex) {
            return this.downgradeGpTier(selectedTier.code);
          }

          return this.previewGpUpgrade(selectedTier.code).pipe(map(() => null));
        }),
        catchError((error) => {
          if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
            return of(null);
          }
          const errorMessage =
            error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_UPGRADING_SUBSCRIPTION';
          this.snackBarService.error(errorMessage);
          return of(null);
        }),
      )
      .subscribe();
  }

  private subscribeGpViaSepa(tierCode: string, paymentMethodId: string): Observable<Subscription | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.stripeService.subscribeGpTier(tierCode, paymentMethodId).pipe(
      switchMap(() => this.subscriptionService.getCurrentSubscription(true)),
      tap((updatedSubscription) => {
        if (!updatedSubscription) {
          return;
        }
        this.currentSubscription = updatedSubscription;
        this.currentStep = 'tier';
        this.pendingSepaPaymentMethodId = null;
        this.sepaTargetTierCode = null;

        if (this.isPaidActivationConfirmed(updatedSubscription, tierCode)) {
          // Reuse LP's trial-aware confirmation (KAP-1855) rather than a
          // generic "upgraded" toast - GP tiers inherit the same
          // trialPeriodDays mechanism from the shared SubscriptionTier base,
          // so a GP trial (if one is ever configured) must not be told it was
          // charged today when it wasn't.
          this.notifySepaSubscribeConfirmed(updatedSubscription);
        } else {
          this.snackBarService.error('INTL_PAYMENT_PENDING_ACTIVATION');
          // KAP-1849 (AC #8): the dialog is about to close and navigate away
          // regardless, so this is the only chance to start watching for the
          // async webhook confirmation - otherwise the tier badge would only
          // ever catch up on some unrelated future page load.
          this.subscriptionService.pollUntilTierActivated(tierCode);
        }
        // Unlike LP's SEPA subscribe, GP always closes and navigates on a
        // successful call - matches the existing gp_free upgradeTier() path,
        // since there is no separate "review your new plan" step for GP.
        this.dialogRef.close(updatedSubscription);
        setTimeout(() => {
          this.navigateToUserWorkflow();
        }, 100);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
          return of(null);
        }
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_SEPA_SUBSCRIBE_FAILED';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  private upgradeGpTierConfirmed(tierCode: string): Observable<Subscription | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.stripeService.upgradeGpTier(tierCode).pipe(
      switchMap(() => this.subscriptionService.getCurrentSubscription(true)),
      tap((updatedSubscription) => {
        if (!updatedSubscription) {
          return;
        }
        this.currentSubscription = updatedSubscription;
        this.snackBarService.success('INTL_SUBSCRIPTION_UPGRADED_SUCCESSFULLY');
        this.dialogRef.close(updatedSubscription);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        // Back to the confirm step (the preview is still on screen), not a fresh preview.
        if (this.handleContractNotSigned(error, () => this.confirmPlanChangeAfterContract())) {
          return of(null);
        }
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_UPGRADING_SUBSCRIPTION';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  private downgradeGpTier(tierCode: string): Observable<Subscription | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.stripeService.downgradeGpTier(tierCode).pipe(
      switchMap(() => this.subscriptionService.getCurrentSubscription(true)),
      tap((updatedSubscription) => {
        if (!updatedSubscription) {
          return;
        }
        this.currentSubscription = updatedSubscription;
        this.snackBarService.success('INTL_DOWNGRADE_SCHEDULED_SUCCESS');
        this.dialogRef.close(updatedSubscription);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
          return of(null);
        }
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_SCHEDULING_DOWNGRADE';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  public onSepaSetupCompleted(paymentMethodId: string): void {
    this.pendingSepaPaymentMethodId = paymentMethodId;
    this.currentStep = 'tier';
    this.cdr.markForCheck();
    if (this.isGP && this.sepaTargetTierCode) {
      this.subscribeGpViaSepa(this.sepaTargetTierCode, paymentMethodId).pipe(untilDestroyed(this)).subscribe();
      return;
    }
    if (this.sepaTargetTierCode) {
      this.subscribeLpViaSepa(this.sepaTargetTierCode, this.sepaTargetBillingInterval, paymentMethodId)
        .pipe(untilDestroyed(this))
        .subscribe();
      return;
    }
    this.onUpgrade();
  }

  public onSepaSetupCancelled(): void {
    this.currentStep = 'tier';
    this.sepaTargetTierCode = null;
    this.sepaChargesNow = false;
    this.cdr.markForCheck();
  }

  public onProceed(): void {
    if (this.isGP) {
      if (this.hasNoSubscriptionError || !this.currentSubscription) {
        return;
      }
      this.ensureOnboardingContract(() => this.completeGpProceed());
    }
  }

  private completeGpProceed(): void {
    this.dialogRef.close(this.currentSubscription || true);
    setTimeout(() => {
      this.navigateToUserWorkflow();
    }, 100);
  }

  private upgradeTier(): void {
    if (!this.selectedTierUid) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.subscriptionService
      .upgradeToTier(this.selectedTierUid)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        switchMap((refreshedSubscription) => {
          this.snackBarService.success('INTL_SUBSCRIPTION_UPGRADED_SUCCESSFULLY');

          if (this.isLP && refreshedSubscription?.isTrialPeriod === true) {
            return this.subscriptionService.getTrialStatus(true).pipe(
              map(() => refreshedSubscription),
              catchError(() => {
                return of(refreshedSubscription);
              }),
            );
          }

          return of(refreshedSubscription);
        }),
        tap((refreshedSubscription) => {
          this.currentSubscription = refreshedSubscription;
          this.cdr.markForCheck();
          if (this.isGP) {
            this.dialogRef.close(refreshedSubscription);
            setTimeout(() => {
              this.navigateToUserWorkflow();
            }, 100);
          } else {
            this.dialogRef.close(refreshedSubscription);
          }
        }),
        catchError((error) => {
          if (this.handleContractNotSigned(error, () => this.upgradeTier())) {
            return of(null);
          }
          const errorMessage =
            error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_UPGRADING_SUBSCRIPTION';
          this.snackBarService.error(errorMessage);
          return of(null);
        }),
      )
      .subscribe();
  }

  private previewGpUpgrade(tierCode: string): Observable<PlanChangePreview | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.subscriptionService.previewGpUpgrade(tierCode).pipe(
      tap((preview) => {
        this.planChangePreview = preview;
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
          return of(null);
        }
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_PLAN_CHANGE_PREVIEW_FAILED';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  private previewUpgrade(tierCode: string, billingInterval: 'annual' | 'monthly'): Observable<PlanChangePreview | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.subscriptionService.previewPlanChange(tierCode, billingInterval).pipe(
      tap((preview) => {
        this.planChangePreview = preview;
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_PLAN_CHANGE_PREVIEW_FAILED';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  private confirmUpgrade(tierCode: string, billingInterval: 'annual' | 'monthly'): Observable<Subscription | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    const confirmRequest$ = this.hasActiveStripeBilling()
      ? this.stripeService.upgradeLpTier(tierCode, billingInterval).pipe(
          switchMap(() => this.reloadSubscriptionAndProfile()),
        )
      : this.subscriptionService.confirmPlanChange(tierCode, billingInterval);

    return confirmRequest$.pipe(
      tap((updatedSubscription) => {
        if (!updatedSubscription) {
          return;
        }
        this.currentSubscription = updatedSubscription;
        this.planChangePreview = null;
        this.subscriptionService.getPendingPlanChange(true).pipe(take(1)).subscribe((pending) => {
          this.pendingPlanChange = pending;
          this.cdr.markForCheck();
        });
        this.notifyUpgradeConfirmed(updatedSubscription);
        this.dialogRef.close(updatedSubscription);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        if (this.handleContractNotSigned(error, () => this.confirmPlanChangeAfterContract())) {
          return of(null);
        }
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_ERROR_UPGRADING_SUBSCRIPTION';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  private scheduleDowngrade(tierCode: string, billingInterval: 'annual' | 'monthly'): Observable<Subscription | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.subscriptionService.schedulePlanChange(tierCode, billingInterval).pipe(
      tap((updatedSubscription) => {
        if (!updatedSubscription) {
          return;
        }
        this.currentSubscription = updatedSubscription;
        this.planChangePreview = null;
        this.pendingPlanChange = updatedSubscription.pendingPlanChange || this.pendingPlanChange;
        // schedulePlanChange has just refreshed the pending change, so this is the cached value.
        this.subscriptionService.getPendingPlanChange().pipe(take(1)).subscribe((pending) => {
          this.pendingPlanChange = pending ?? this.pendingPlanChange;
        });
        this.notifyDowngradeScheduled(this.pendingPlanChange);
        this.dialogRef.close(updatedSubscription);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        // A change is already pending. Rather than showing a dead-end error,
        // surface the blocking change so the existing "Cancel scheduled change"
        // action becomes reachable — QA reported this as having no way out.
        if (error?.error?.code === 'plan_change_already_scheduled') {
          const blocking = this.subscriptionService.mapPendingPlanChange(error.error.pendingChange);
          if (blocking) {
            this.pendingPlanChange = blocking;
          }
          this.snackBarService.error('INTL_PLAN_CHANGE_ALREADY_SCHEDULED');
          this.cdr.markForCheck();
          return of(null);
        }

        if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
          return of(null);
        }

        const errorMessage =
          error?.error?.error ||
          error?.error?.detail ||
          error?.error?.message ||
          'INTL_ERROR_SCHEDULING_DOWNGRADE';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  /**
   * KAP-1892: an issuer's scheduled downgrade arrives with the subscription itself
   * (`pendingPlanChange`) - there is no separate pending-change endpoint like the
   * investor one - so the same banner and Cancel button work for both.
   */
  private syncGpPendingPlanChange(): void {
    if (this.isGP) {
      this.pendingPlanChange = this.currentSubscription?.pendingPlanChange ?? null;
    }
  }

  private loadPendingPlanChange(): void {
    this.subscriptionService
      .getPendingPlanChange()
      .pipe(
        untilDestroyed(this),
        take(1),
      )
      .subscribe((pending) => {
        this.pendingPlanChange = pending;
        this.cdr.markForCheck();
      });
  }

  public onCancelScheduledDowngrade(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.subscriptionService
      .cancelScheduledPlanChange()
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (updatedSubscription) => {
          this.pendingPlanChange = null;
          this.planChangePreview = null;
          this.currentSubscription = updatedSubscription;
          this.snackBarService.success('INTL_SCHEDULED_CHANGE_CANCELLED');
        },
        error: (error) => {
          const errorMessage =
            error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_CANCEL_SCHEDULED_CHANGE_FAILED';
          this.snackBarService.error(errorMessage);
        },
      });
  }

  public onCancel(): void {
    if (this.currentStep === 'contract') {
      // Back out to tier selection; envelope stays pending server-side for reuse.
      this.onContractCancelled();
      return;
    }
    this.dialogRef.close(false);
  }

  /** Leave the contract step without closing the dialog (KAP-1876). */
  public onContractCancelled(): void {
    this.pendingAfterContract = null;
    this.leaveContractStep();
    this.cdr.markForCheck();
  }

  public onContractSignedContinue(): void {
    this.checkContractSignedOnce({ manual: true });
  }

  public onContractRetry(): void {
    this.contractSigningUnavailable = false;
    this.isContractBusy = true;
    this.cdr.markForCheck();
    this.onboardingContractService
      .createEnvelope()
      .pipe(
        take(1),
        finalize(() => {
          this.isContractBusy = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (created) => {
          if (hasSignedOnboardingContract(created)) {
            this.onContractConfirmedSigned();
            return;
          }
          this.contractSigningEmbed = created.envelopeSigningEmbed;
          this.startContractPolling();
        },
        error: (error) => {
          if (isOnboardingContractTemplateMissing(error)) {
            this.blockForMissingContractTemplate(true);
            return;
          }
          if (isOnboardingContractSigningUnavailable(error)) {
            this.contractSigningUnavailable = true;
            this.snackBarService.error('INTL_ONBOARDING_CONTRACT_SIGNING_UNAVAILABLE');
            return;
          }
          if (isOnboardingContractNoOrganization(error)) {
            this.snackBarService.error('INTL_ONBOARDING_CONTRACT_NO_ORGANIZATION');
            return;
          }
          this.snackBarService.error('INTL_ONBOARDING_CONTRACT_SIGNING_UNAVAILABLE');
        },
      });
  }

  private ensureOnboardingContract(onReady: () => void): void {
    if (this.hasConfirmedSignedContract) {
      onReady();
      return;
    }

    // Templates not seeded: KAP-1874 still rejects tier mutations. Never "skip and proceed".
    if (this.contractTemplateMissing) {
      return;
    }

    if (this.isEnsuringOnboardingContract) {
      return;
    }

    this.isEnsuringOnboardingContract = true;
    this.isLoading = true;
    this.cdr.markForCheck();
    this.onboardingContractService
      .getStatus()
      .pipe(
        switchMap((status) => {
          if (hasSignedOnboardingContract(status)) {
            return of({ kind: 'signed' as const });
          }
          return this.onboardingContractService.createEnvelope().pipe(
            map((created) =>
              hasSignedOnboardingContract(created)
                ? { kind: 'signed' as const }
                : { kind: 'embed' as const, embed: created.envelopeSigningEmbed },
            ),
            catchError((error) => of({ kind: 'createError' as const, error })),
          );
        }),
        catchError((error) => of({ kind: 'statusError' as const, error })),
        finalize(() => {
          this.isEnsuringOnboardingContract = false;
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        if (result.kind === 'signed') {
          this.hasConfirmedSignedContract = true;
          this.contractTemplateMissing = false;
          this.isEnsuringOnboardingContract = false;
          onReady();
          return;
        }
        if (result.kind === 'embed') {
          this.isEnsuringOnboardingContract = false;
          this.enterContractStep(result.embed, onReady);
          return;
        }
        if (result.kind === 'createError' && isOnboardingContractTemplateMissing(result.error)) {
          this.isEnsuringOnboardingContract = false;
          this.blockForMissingContractTemplate(true);
          return;
        }
        if (result.kind === 'statusError' && isOnboardingContractTemplateMissing(result.error)) {
          this.isEnsuringOnboardingContract = false;
          this.blockForMissingContractTemplate(true);
          return;
        }
        if (result.kind === 'createError' && isOnboardingContractSigningUnavailable(result.error)) {
          this.isEnsuringOnboardingContract = false;
          this.enterContractStep(null, onReady);
          this.contractSigningUnavailable = true;
          this.snackBarService.error('INTL_ONBOARDING_CONTRACT_SIGNING_UNAVAILABLE');
          return;
        }
        if (isOnboardingContractNoOrganization(result.error)) {
          this.snackBarService.error('INTL_ONBOARDING_CONTRACT_NO_ORGANIZATION');
          return;
        }
        this.snackBarService.error('INTL_ONBOARDING_CONTRACT_SIGNING_UNAVAILABLE');
      });
  }

  private blockForMissingContractTemplate(showSnackbar: boolean): void {
    const wasAlreadyBlocked = this.contractTemplateMissing;
    this.contractTemplateMissing = true;
    this.hasConfirmedSignedContract = false;
    this.pendingAfterContract = null;
    this.stopContractPolling();
    if (this.currentStep === 'contract') {
      this.leaveContractStep();
    }
    if (showSnackbar && !wasAlreadyBlocked) {
      this.snackBarService.error('INTL_ONBOARDING_CONTRACT_TEMPLATE_MISSING');
    }
    this.cdr.markForCheck();
  }

  private enterContractStep(embed: string | null, onReady: () => void): void {
    this.pendingAfterContract = onReady;
    this.contractSigningEmbed = embed;
    this.contractSigningUnavailable = false;
    this.previousDisableClose = this.dialogRef.disableClose ?? true;
    this.dialogRef.disableClose = true;
    this.currentStep = 'contract';
    this.cdr.markForCheck();
    if (embed) {
      this.startContractPolling();
    }
  }

  private leaveContractStep(): void {
    this.stopContractPolling();
    this.currentStep = 'tier';
    this.contractSigningEmbed = null;
    this.contractSigningUnavailable = false;
    this.dialogRef.disableClose = this.previousDisableClose;
    this.cdr.markForCheck();
  }

  private checkContractSignedOnce(options: { manual?: boolean } = {}): void {
    const manual = options.manual ?? false;
    if (manual) {
      this.isContractBusy = true;
      this.cdr.markForCheck();
    }

    this.onboardingContractService
      .getStatus({ fresh: true })
      .pipe(
        take(1),
        catchError(() => of(null)),
        finalize(() => {
          if (manual) {
            this.isContractBusy = false;
            this.cdr.markForCheck();
          }
        }),
        untilDestroyed(this),
      )
      .subscribe((status) => {
        if (status && hasSignedOnboardingContract(status)) {
          this.onContractConfirmedSigned();
          return;
        }
        if (manual) {
          this.snackBarService.error('INTL_ONBOARDING_CONTRACT_NOT_YET_SIGNED');
        }
      });
  }

  private startContractPolling(): void {
    this.stopContractPolling();
    this.contractPollSub = timer(0, this.contractPollMs)
      .pipe(
        filter(() => this.currentStep === 'contract' && !this.isContractBusy),
        exhaustMap(() =>
          this.onboardingContractService.getStatus({ fresh: true }).pipe(
            catchError(() => of(null)),
            take(1),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((status) => {
        if (status && hasSignedOnboardingContract(status)) {
          this.onContractConfirmedSigned();
        }
      });
  }

  private stopContractPolling(): void {
    this.contractPollSub?.unsubscribe();
    this.contractPollSub = null;
  }

  private onContractConfirmedSigned(): void {
    this.hasConfirmedSignedContract = true;
    this.stopContractPolling();
    this.subscriptionService
      .getCurrentSubscription(true)
      .pipe(
        catchError(() => of(this.currentSubscription)),
        take(1),
        untilDestroyed(this),
      )
      .subscribe((subscription) => {
        if (subscription) {
          this.currentSubscription = subscription;
        }
        this.leaveContractStep();
        this.runPendingAfterContract();
      });
  }

  private runPendingAfterContract(): void {
    const next = this.pendingAfterContract;
    this.pendingAfterContract = null;
    if (next) {
      next();
    }
  }

  private handleContractNotSigned(error: unknown, retry: () => void): boolean {
    if (!isOnboardingContractNotSigned(error)) {
      return false;
    }
    this.hasConfirmedSignedContract = false;
    // Backend requires a signed contract, but this env has no template to create one.
    // Do not re-enter ensure → subscribe → 403 (that loop floods the network).
    if (this.contractTemplateMissing) {
      return true;
    }
    this.ensureOnboardingContract(retry);
    return true;
  }

  private shouldUseSepaSubscribeFlow(selectedTier: SubscriptionTier): boolean {
    const isPaidTier = selectedTier.code !== 'lp_free';
    if (!isPaidTier) {
      return false;
    }
    if (!this.currentSubscription || this.hasNoSubscriptionError) {
      return true;
    }
    if (this.currentSubscription.tier?.code === 'lp_free') {
      return true;
    }
    // A trial without Stripe billing (the legacy internal trial) collects SEPA first.
    // A Stripe trial changes tier in place and keeps running, with nothing charged
    // and no bank details needed, so it takes the preview/confirm path (KAP-1897).
    if (this.isOnTrialPeriod() && !this.hasActiveStripeBilling()) {
      return true;
    }
    // Reactivating the current tier re-subscribes fresh (the backend supersedes
    // the old/failing subscription), so collect a SEPA mandate now. Must come
    // before hasActiveStripeBilling, which counts past_due as active billing.
    if (
      this.canReactivateCurrentTier() &&
      selectedTier.uid === this.currentSubscription?.tier?.uid
    ) {
      return true;
    }
    if (!this.hasActiveStripeBilling()) {
      return true;
    }
    return false;
  }

  private hasActiveStripeBilling(): boolean {
    const subscription = this.currentSubscription;
    if (!subscription) {
      return false;
    }
    if (subscription.hasStripeBilling === true) {
      return true;
    }
    if (subscription.hasStripeBilling === false) {
      return false;
    }
    const paymentStatus = (subscription.paymentStatus || '').toLowerCase();
    return ['active', 'trialing', 'trial', 'past_due'].includes(paymentStatus);
  }

  private isOnTrialPeriod(): boolean {
    const subscription = this.currentSubscription;
    if (!subscription) {
      return false;
    }
    return subscription.isTrialPeriod === true || subscription.isTrial === true || subscription.status === 'trial';
  }

  /**
   * LP paid subscribe once a tier is chosen. A tier that starts a free trial
   * subscribes straight away with no SEPA step — bank details are collected
   * before the trial ends (KAP-1897). Anything charged now still collects SEPA
   * first. The backend has the final say on trial eligibility: if it asks for a
   * payment method, `subscribeLpViaSepa` drops back to the SEPA step.
   */
  private startLpSepaSubscribe(
    selectedTier: SubscriptionTier,
    billingInterval: 'annual' | 'monthly',
  ): Observable<Subscription | null> {
    if (this.pendingSepaPaymentMethodId) {
      const targetTierCode = this.sepaTargetTierCode || selectedTier.code;
      const targetBillingInterval = this.sepaTargetTierCode ? this.sepaTargetBillingInterval : billingInterval;
      return this.subscribeLpViaSepa(targetTierCode, targetBillingInterval, this.pendingSepaPaymentMethodId);
    }
    if (this.canStartTrialWithoutPaymentMethod(selectedTier)) {
      return this.subscribeLpViaSepa(selectedTier.code, billingInterval, null);
    }
    this.openSepaStep(selectedTier.code, billingInterval);
    return of(null);
  }

  private canStartTrialWithoutPaymentMethod(tier: SubscriptionTier): boolean {
    if (!this.isTrialAvailableForTier(tier)) {
      return false;
    }
    // Upgrading mid-trial ends the trial and charges now — one trial per org.
    if (this.isOnTrialPeriod()) {
      return false;
    }
    // Reactivating a lapsed tier is charged now, never re-trialed (KAP-1866).
    if (this.canReactivateCurrentTier() && tier.uid === this.currentSubscription?.tier?.uid) {
      return false;
    }
    return true;
  }

  /**
   * `chargesNow` is set when the backend has already said no trial applies, so the
   * SEPA step does not promise a first-payment date at the end of a trial.
   */
  private openSepaStep(tierCode: string, billingInterval: 'annual' | 'monthly', chargesNow = false): void {
    this.sepaTargetTierCode = tierCode;
    this.sepaTargetBillingInterval = billingInterval;
    this.sepaChargesNow = chargesNow;
    this.currentStep = 'sepa';
    this.cdr.markForCheck();
  }

  /** `paymentMethodId` is null for a trial started without SEPA details (KAP-1897). */
  private subscribeLpViaSepa(
    tierCode: string,
    billingInterval: 'annual' | 'monthly',
    paymentMethodId: string | null,
  ): Observable<Subscription | null> {
    this.isLoading = true;
    this.cdr.markForCheck();

    return this.stripeService.subscribeLpTier(tierCode, billingInterval, paymentMethodId).pipe(
      switchMap(() => this.reloadSubscriptionAndProfile()),
      switchMap((refreshedSubscription) => {
        if (refreshedSubscription?.isTrialPeriod === true) {
          return this.subscriptionService.getTrialStatus(true).pipe(
            map(() => refreshedSubscription),
            catchError(() => of(refreshedSubscription)),
          );
        }
        return of(refreshedSubscription);
      }),
      tap((updatedSubscription) => {
        if (!updatedSubscription) {
          return;
        }
        this.currentSubscription = updatedSubscription;
        this.currentStep = 'tier';

        if (this.isPaidActivationConfirmed(updatedSubscription, tierCode)) {
          this.pendingSepaPaymentMethodId = null;
          this.sepaTargetTierCode = null;
          this.sepaChargesNow = false;
          this.planChangePreview = null;
          this.notifySepaSubscribeConfirmed(updatedSubscription);
          this.dialogRef.close(updatedSubscription);
          return;
        }

        this.pendingSepaPaymentMethodId = null;
        this.sepaTargetTierCode = null;
        this.sepaChargesNow = false;
        this.snackBarService.error('INTL_PAYMENT_PENDING_ACTIVATION');
        // KAP-1849 (AC #8): even though the dialog stays open here (unlike
        // GP's SEPA flow), nothing re-checks confirmation on its own - the
        // user may still close the dialog or navigate away before it
        // resolves, so the same tier-badge staleness applies.
        this.subscriptionService.pollUntilTierActivated(tierCode);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      catchError((error) => {
        if (this.handleContractNotSigned(error, () => this.continueUpgradeAfterContract())) {
          return of(null);
        }
        // No trial for this org after all (already used, or none on the tier):
        // the first payment is due now, so collect SEPA and resubmit from there.
        if (paymentMethodId === null && isPaymentMethodRequiredError(error)) {
          this.openSepaStep(tierCode, billingInterval, true);
          return of(null);
        }
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || 'INTL_SEPA_SUBSCRIBE_FAILED';
        this.snackBarService.error(errorMessage);
        return of(null);
      }),
    );
  }

  /** The profile holds the usage limits the app gates on, so it follows the subscription (KAP-1897). */
  private reloadSubscriptionAndProfile(): Observable<Subscription | null> {
    return this.subscriptionService.getCurrentSubscription(true).pipe(
      switchMap((subscription) => this.subscriptionService.refreshProfile().pipe(map(() => subscription))),
    );
  }

  private isPaidActivationConfirmed(subscription: Subscription, targetTierCode: string): boolean {
    const normalizedPaymentStatus = (subscription.paymentStatus || '').toLowerCase();
    const confirmedStatuses = ['active', 'trial', 'trialing'];
    const isTargetTierApplied = subscription.tier?.code === targetTierCode;
    // A trial on any paid tier counts as activated — the tier is granted up front
    // and nothing is charged until the trial ends (KAP-1853). Previously restricted
    // to `lp_standard`, which left Professional/Enterprise trials reading as
    // unconfirmed.
    const isTrialActivated =
      isTargetTierApplied &&
      (subscription.isTrialPeriod === true || subscription.isTrial === true || subscription.status === 'trial');
    if (isTrialActivated) {
      return true;
    }
    return isTargetTierApplied && confirmedStatuses.includes(normalizedPaymentStatus);
  }

  /**
   * Success confirmation after a SEPA subscribe. Surfaces the *authoritative*
   * first-payment date from the refreshed subscription (`trialEndDate`, set
   * server-side) rather than the estimate shown on the entry screen — the
   * reconciliation half of KAP-1855. Without a trial the debit was initiated
   * immediately, so we confirm that instead of showing a date. A trial started
   * without SEPA details is told to add them by that date instead (KAP-1897).
   */
  private notifySepaSubscribeConfirmed(subscription: Subscription): void {
    if (subscription.isTrialPeriod && subscription.trialEndDate) {
      try {
        const date = formatDate(subscription.trialEndDate, 'longDate', this.locale);
        const messageKey = subscription.paymentMethodRequired
          ? 'INTL_TRIAL_STARTED_ADD_BANK_DETAILS_BY'
          : 'INTL_SUBSCRIPTION_CONFIRMED_FIRST_PAYMENT_ON';
        this.snackBarService.success(messageKey, { date });
        return;
      } catch {
        // Locale/format edge case must never block the confirmation itself.
        this.snackBarService.success('INTL_SUBSCRIPTION_UPGRADED_SUCCESSFULLY');
        return;
      }
    }
    this.snackBarService.success('INTL_SUBSCRIPTION_CONFIRMED_FIRST_PAYMENT_TODAY');
  }

  /** An upgrade during a trial keeps the trial (KAP-1897), so say until when. */
  private notifyUpgradeConfirmed(subscription: Subscription): void {
    const date = subscription.isTrialPeriod ? this.formatLongDate(subscription.trialEndDate) : null;
    if (date) {
      this.snackBarService.success('INTL_TRIAL_CONTINUES_ON_TIER', { tier: subscription.tier?.name, date });
      return;
    }
    this.snackBarService.success('INTL_SUBSCRIPTION_UPGRADED_SUCCESSFULLY');
  }

  /** Name the tier and the day it takes effect, not just "end of the period" (KAP-1897). */
  private notifyDowngradeScheduled(pending: PendingPlanChange | null): void {
    const date = this.formatLongDate(pending?.scheduledChangeAt);
    if (date && pending?.pendingTier?.name) {
      this.snackBarService.success('INTL_DOWNGRADE_SCHEDULED_ON', { tier: pending.pendingTier.name, date });
      return;
    }
    this.snackBarService.success('INTL_DOWNGRADE_SCHEDULED_SUCCESS');
  }

  private formatLongDate(value: string | Date | null | undefined): string | null {
    if (!value) {
      return null;
    }
    try {
      return formatDate(value, 'longDate', this.locale);
    } catch {
      // Locale/format edge case: the caller falls back to an undated message.
      return null;
    }
  }

  public canUpgrade(): boolean {
    if (this.contractTemplateMissing) {
      return false;
    }

    if (!this.selectedTierUid) {
      return false;
    }

    if (this.isGP || this.isLP) {
      if (!this.currentSubscription && this.hasNoSubscriptionError) {
        return true;
      }
      if (!this.currentSubscription) {
        // GP's currentSubscription is always populated by autoUpgradeGPToFreeTier()
        // shortly after open, but this covers the brief pre-load window.
        return this.isGP;
      }
      // Same tier is normally a no-op, but an expired or payment-blocked
      // subscription may re-pay (reactivate) the tier it is already on.
      return (
        this.selectedTierUid !== this.currentSubscription.tier.uid ||
        this.canReactivateCurrentTier()
      );
    }

    return false;
  }

  /**
   * The current tier is re-payable when the subscription has expired or its
   * payment is blocked. In both states the tier card would otherwise render a
   * locked "Active" badge with no way to pay again — QA hit exactly this after
   * a failed debit ("click active button > nothing change", KAP-1866).
   * Only ever offered for a paid tier: "reactivating" Free is meaningless, and
   * a canceled/expired sub that already sits on lp_free upgrades instead.
   */
  public canReactivateCurrentTier(): boolean {
    if (isFreeTierSubscription(this.currentSubscription)) {
      return false;
    }
    return this.isCurrentSubscriptionExpired() || this.isCurrentSubscriptionBlocked();
  }

  /**
   * Whether paid access is withheld because of a failed or dead payment.
   * `accessState` is the backend's computed access verdict; `paymentStatus`
   * (mirrors Stripe) is the fallback for payloads that predate accessState.
   * `canceled` is included because a half-failed re-subscribe (old subscription
   * superseded, new Stripe create errored) leaves the org on a paid tier with
   * canceled access — without it that state is another dead end. Deliberately
   * does NOT include `payment_processing` — that is "waiting for the bank",
   * not a dead end, and re-subscribing there would double-charge.
   */
  public isCurrentSubscriptionBlocked(): boolean {
    const subscription = this.currentSubscription;
    if (!subscription) {
      return false;
    }
    if (
      subscription.accessState === 'past_due' ||
      subscription.accessState === 'suspended' ||
      subscription.accessState === 'canceled'
    ) {
      return true;
    }
    const paymentStatus = (subscription.paymentStatus || '').toLowerCase();
    return ['past_due', 'suspended', 'unpaid', 'canceled'].includes(paymentStatus);
  }

  /**
   * An expired subscription (past its `end_date`) still points at its old paid
   * tier, so the dialog would otherwise show that tier as a locked "Active"
   * badge with no way to pay again. Surface expiry so the current tier becomes
   * a "Reactivate" action. `/current/` already carries the backend-computed
   * `status` ('expired') and `endDate`; prefer status and fall back to a date
   * check.
   */
  public isCurrentSubscriptionExpired(): boolean {
    const subscription = this.currentSubscription;
    if (!subscription) {
      return false;
    }
    if (subscription.status === 'expired') {
      return true;
    }
    if (subscription.endDate) {
      const end = new Date(subscription.endDate);
      if (!isNaN(end.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return end < today;
      }
    }
    return false;
  }

  public canSubscribe(): boolean {
    if (this.contractTemplateMissing) {
      return false;
    }
    // KAP-1849: once paid GP tiers have their own per-tier button
    // (continueGPUpgradeAfterContract), the bottom "Continue" button is only
    // meaningful when the org is ALREADY on gp_free - there's nothing to
    // change, so completeGpProceed() just closes/confirms. It must never be
    // reachable for gp_free selected FROM a paid tier: completeGpProceed()
    // doesn't call any upgrade/downgrade API, so that combination used to
    // close the dialog as if a downgrade happened when nothing changed. That
    // case now belongs solely to the tier card's own button, which routes
    // through the real (billing-aware) downgrade flow.
    //
    // isResolvingSubscription also guards the !currentSubscription branch: while a
    // brand-new org's subscription is still loading or performGpFreeAutoUpgrade() is
    // in flight, onProceed() intentionally no-ops (closing the dialog now would
    // unsubscribe untilDestroyed(this) and abort the auto-upgrade) — showing this
    // button as clickable during that window would be a dead control.
    return (
      this.isGP &&
      !this.isResolvingSubscription &&
      this.selectedTierUid !== null &&
      this.selectedTierCode === 'gp_free' &&
      (!this.currentSubscription || this.currentSubscription.tier?.code === 'gp_free')
    );
  }

  private navigateToUserWorkflow(): void {
    this.appStoreQuery
      .selectUser()
      .pipe(
        take(1),
        withLatestFrom(this.routerService.selectCurrentRoute()),
        untilDestroyed(this),
      )
      .subscribe(([user, routeArray]) => {
        const routeString = routeArray ? routeArray.join('/') : '';

        if (user?.isStaff) {
          this.router.navigate(['superuser']);
        } else if (user?.isRestrictedUser) {
          this.router.navigate(['pending-area']);
        } else {
          if (routeString && routeString.includes(partnerRoute)) {
            this.router.navigate([selectPartnerCompanyAbsoluteRoute]);
          } else {
            this.organizationQuery
              .selectChosenOrganizationType()
              .pipe(take(1))
              .subscribe((orgType) => {
                if (orgType === 'emittent') {
                  this.router.navigate([productRoute]);
                } else {
                  this.router.navigate([selectCompanyAbsoluteRoute]);
                }
              });
          }
        }
      });
  }

  public getTierIcon(tierCode: string): string {
    const icons: Record<'free' | 'standard' | 'professional' | 'enterprise', string> = {
      free: 'free_breakfast',
      standard: 'star',
      professional: 'workspace_premium',
      enterprise: 'diamond',
    };
    return icons[this.getTierVisualSlot(tierCode)];
  }

  public getSortedTiers(tiers: SubscriptionTier[]): SubscriptionTier[] {
    // this.isGP, not tiers[0]?.tierType - the component's own authoritative
    // flag, so an empty/reordered/filtered tiers array can't silently fall
    // back to the wrong order.
    const tierOrder = this.isGP ? this.gpTierOrder : ['lp_free', 'lp_standard', 'lp_professional', 'lp_enterprise'];
    return [...tiers].sort((a, b) => {
      const indexA = tierOrder.indexOf(a.code);
      const indexB = tierOrder.indexOf(b.code);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  /**
   * Parse description field that may contain \r\n line breaks and return as array.
   */
  public getDescriptionList(description: string | null | undefined): string[] {
    if (!description) {
      return [];
    }

    const items = description
      .split(/\r\n|\r|\n/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return items.length > 0 ? items : [description];
  }

  /**
   * Get the button text for a tier based on current subscription and tier code.
   */
  public getButtonText(tier: SubscriptionTier): string {
    if (this.isGP && this.currentSubscription && tier.uid === this.currentSubscription.tier.uid) {
      return this.canReactivateCurrentTier() ? 'INTL_REACTIVATE' : 'INTL_ACTIVE';
    }

    if (this.isGP && this.currentSubscription) {
      const currentTierCode = this.currentSubscription.tier?.code;
      const currentIndex = this.gpTierOrder.indexOf(currentTierCode);
      const targetIndex = this.gpTierOrder.indexOf(tier.code);

      if (currentIndex !== -1 && targetIndex !== -1 && targetIndex < currentIndex) {
        return 'INTL_SCHEDULE_DOWNGRADE';
      }
      if (currentIndex !== -1 && targetIndex !== -1 && targetIndex > currentIndex) {
        return 'INTL_UPGRADE';
      }
    }

    if (this.isLP && this.currentSubscription && tier.uid === this.currentSubscription.tier.uid) {
      // An expired or payment-blocked subscription still points at its old paid
      // tier. Offer to pay again (reactivate) the same tier instead of a locked
      // "Active" badge.
      return this.canReactivateCurrentTier() ? 'INTL_REACTIVATE' : 'INTL_ACTIVE';
    }

    if (this.isLP && this.shouldUseSepaSubscribeFlow(tier) && !this.pendingSepaPaymentMethodId) {
      return 'INTL_UPGRADE';
    }

    if (this.isLP && this.currentSubscription) {
      const currentTierCode = this.currentSubscription.tier?.code;
      const tierOrder = ['lp_free', 'lp_standard', 'lp_professional', 'lp_enterprise'];
      const currentIndex = tierOrder.indexOf(currentTierCode);
      const targetIndex = tierOrder.indexOf(tier.code);

      if (currentIndex !== -1 && targetIndex !== -1 && targetIndex < currentIndex) {
        // A translation key, like every other branch here. This used to be the
        // bare string 'Schedule Downgrade', which the `| translate` pipe echoes
        // verbatim — so the button was never translated and had no German.
        return 'INTL_SCHEDULE_DOWNGRADE';
      }

      const billingInterval = this.selectedBillingInterval || this.currentSubscription.billingInterval || 'annual';
      const hasMatchingPreview =
        this.planChangePreview &&
        this.planChangePreview.targetTier?.code === tier.code &&
        this.planChangePreview.billingInterval === billingInterval;
      if (hasMatchingPreview) {
        return 'INTL_CONFIRM';
      }
      if (currentIndex !== -1 && targetIndex !== -1 && targetIndex > currentIndex) {
        return 'INTL_UPGRADE';
      }
    }

    if (this.isTrialAvailableForTier(tier)) {
      return 'INTL_START_FREE_TRIAL';
    }

    return 'INTL_SUBSCRIBE';
  }

  /**
   * Whether this tier starts a free trial when subscribed to.
   *
   * `isTrialAvailable` is set by the API from the tier's `trial_period_days`. It is
   * absent for tiers with no trial, so a plain truthy check is what we want — the
   * previous `code === 'lp_standard' && isTrialAvailable !== false` treated a missing
   * flag as "trial available" and would now grant the badge to every tier.
   */
  public isTrialAvailableForTier(tier: SubscriptionTier | null): boolean {
    return tier?.isTrialAvailable === true;
  }

  /**
   * Configured free-trial length for a tier, in days, or 0 when the tier has no
   * trial. Every trial-duration string in the dialog derives from this so the copy
   * tracks the tier's `trial_period_days` instead of a hard-coded "1 month"
   * (KAP-1854): raise the configured value and the popup follows; clear it and the
   * trial messaging disappears. Requires both the `isTrialAvailable` flag and a
   * positive day count so we never claim a duration we don't actually have.
   */
  public getTrialPeriodDays(tier: SubscriptionTier | null | undefined): number {
    if (!this.isTrialAvailableForTier(tier ?? null)) {
      return 0;
    }
    const days = tier?.trialPeriodDays ?? 0;
    return days > 0 ? days : 0;
  }

  /**
   * Get the next tier based on current subscription tier.
   */
  private getNextTier(tiers: SubscriptionTier[]): SubscriptionTier | null {
    if (!this.currentSubscription || !tiers || tiers.length === 0) {
      return null;
    }

    const currentTierCode = this.currentSubscription.tier.code;
    const tierOrder = ['lp_free', 'lp_standard', 'lp_professional', 'lp_enterprise'];
    const currentIndex = tierOrder.indexOf(currentTierCode);

    if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
      return null;
    }

    const nextTierCode = tierOrder[currentIndex + 1];
    return tiers.find((t) => t.code === nextTierCode) || null;
  }

  /**
   * Handle subscription updates from GP addon selector.
   */
  public onSubscriptionUpdated(updatedSubscription: Subscription): void {
    this.currentSubscription = updatedSubscription;
    this.cdr.markForCheck();
    if (this.isGP) {
      return;
    }
    this.dialogRef.close(updatedSubscription);
  }
}
